const RECOMMENDED_VOICE_RANKINGS = [
  { rank: 0, match: (name) => name.includes("Microsoft Ava Online (Natural)") }, // Preferred default Edge voice.
  {
    rank: 1,
    match: (name) =>
      /^Microsoft (\S+ )*Online \(Natural\).*\(United States\)/.test(name),
  }, // All other US Natural Edge voices.
  {
    rank: 2,
    match: (name) =>
      /^Microsoft (\S+ )*Online \(Natural\).*\(United Kingdom\)/.test(name),
  }, // All UK Natural Edge voices.
  {
    rank: 3,
    match: (name) =>
      /^Microsoft (\S+ )*Online \(Natural\).*\(India\)/.test(name),
  }, // All India Natural Edge voices.
  {
    rank: 4,
    sortByCountryCode: true,
    match: (name) => /^Microsoft (\S+ )*Online \(Natural\).*/.test(name),
  }, // All other Edge voices.
  { rank: 5, match: (name) => name === "Google US English 3 (Natural)" }, // Chrome OS, Google Natural voices installed.  Prefer Google US voice #3 as default voice.
  {
    rank: 6,
    match: (name, allNames) =>
      name === "Chrome OS US English 4" &&
      !allNames.some((n) => n.match(/^Google US English \d+ \(Natural\)$/)),
  }, // Chrome OS, Google US English Natural voices not installed.  Prefer Chrome OS voice #4 as default voice
  {
    rank: 7,
    match: (name) => name.match(/^Google \S+ English \d+ \(Natural\)$/),
  }, // Chrome OS, Google Natural voices installed.  All other Google English voices.
  {
    rank: 8,
    match: (name) => name.match(/^Chrome OS \S+ English \d+$/),
  }, // Chrome OS, Google Natural voices not installed.  All other Chrome OS voices.
  { rank: 9, match: (name) => name.includes("Premium") }, // macOS + Chrome / Firefox, premium voices installed.
  { rank: 10, match: (name) => name.includes("Enhanced") }, // macOS + Chrome / Firefox, enhanced voices installed.
  { rank: 11, match: (name) => name === "Samantha" }, // Safari or Firefox on macOS
  { rank: 12, match: (name) => name.includes("Microsoft Zira") }, // Firefox / Chrome on Windows
  { rank: 13, match: (name) => name === "Google US English" }, // High quality, but loses word highlighting so make sure it doesn't default to top
  { rank: 14, match: (name) => name.match(/^Google \S+ English \S+/) }, // High quality, but loses word highlighting so make sure it doesn't default to top, Non US voices
];

function getVoiceRank(name, allNames) {
  return (
    RECOMMENDED_VOICE_RANKINGS.find(({ match }) => match(name, allNames)) ??
    null
  );
}

export const NOVELTY_VOICE_NAMES = new Set([
  "Albert",
  "Bad News",
  "Bahh",
  "Bells",
  "Boing",
  "Bubbles",
  "Cellos",
  "Good News",
  "Jester",
  "Organ",
  "Superstar",
  "Trinoids",
  "Whisper",
  "Wobble",
  "Zarvox",
]);

export const LOW_QUALITY_VOICE_NAMES = new Set([
  // Eloquence (Apple)
  "Eddy",
  "Flo",
  "Grandma",
  "Grandpa",
  "Jacques",
  "Reed",
  "Rocko",
  "Sandy",
  "Shelley",
  // Legacy Apple
  "Fred",
  "Junior",
  "Kathy",
  "Ralph",
  // eSpeak (Chrome OS)
  "eSpeak Arabic",
  "eSpeak Bulgarian",
  "eSpeak Bengali",
  "eSpeak Catalan",
  "eSpeak Chinese (Mandarin, latin as English)",
  "eSpeak Czech",
  "eSpeak Danish",
  "eSpeak German",
  "eSpeak Greek",
  "eSpeak Spanish (Spain)",
  "eSpeak Estonian",
  "eSpeak Finnish",
  "eSpeak Gujarati",
  "eSpeak Croatian",
  "eSpeak Hungarian",
  "eSpeak Indonesian",
  "eSpeak Italian",
  "eSpeak Kannada",
  "eSpeak Korean",
  "eSpeak Lithuanian",
  "eSpeak Latvian",
  "eSpeak Malayalm",
  "eSpeak Marathi",
  "eSpeak Malay",
  "eSpeak Norwegian Bokmål",
  "eSpeak Polish",
  "eSpeak Portuguese (Brazil)",
  "eSpeak Romanian",
  "eSpeak Russian",
  "eSpeak Slovak",
  "eSpeak Slovenian",
  "eSpeak Serbian",
  "eSpeak Swedish",
  "eSpeak Swahili",
  "eSpeak Tamil",
  "eSpeak Telugu",
  "eSpeak Turkish",
  "eSpeak Vietnamese (Northern)",
]);

/**
 *
 * @param {Array<SpeechSynthesisVoice>} voices
 * @param {Array<string>} languages ISO language codes
 * @param {string} fallbackLanguage
 * @returns {Promise<{highQuality: SpeechSynthesisVoice[], standard: SpeechSynthesisVoice[], other: SpeechSynthesisVoice[]}>}
 */
export async function rankAndCategorizeVoices(
  voices,
  languages,
  fallbackLanguage,
) {
  if (voices.length === 0) {
    voices = getFilteredVoices(voices, languages, fallbackLanguage);
  }

  const rankedVoices = [];
  const standardVoices = [];
  const otherVoices = [];

  const allNames = voices.map((v) => v.name);

  for (const voice of voices) {
    const ranking = getVoiceRank(voice.name, allNames);
    if (ranking !== null) {
      rankedVoices.push({
        voice,
        rank: ranking.rank,
        sortByCountryCode: ranking.sortByCountryCode,
      });
    } else {
      if (
        [...LOW_QUALITY_VOICE_NAMES, ...NOVELTY_VOICE_NAMES].some((n) =>
          voice.name.includes(n),
        )
      ) {
        otherVoices.push(voice);
      } else {
        standardVoices.push(voice);
      }
    }
  }

  const byName = (a, b) => a.name.localeCompare(b.name);

  const getVoiceCountry = (name) => name.match(/\(([^)]+)\)$/)?.[1] ?? "";

  return {
    highQuality: rankedVoices
      .sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.sortByCountryCode) {
          const countryCompare = getVoiceCountry(a.voice.name).localeCompare(
            getVoiceCountry(b.voice.name),
          );
          if (countryCompare !== 0) return countryCompare;
        }
        return a.voice.name.localeCompare(b.voice.name);
      })
      .map(({ voice }) => voice),
    standard: standardVoices.sort(byName),
    other: otherVoices.sort(byName),
  };
}

/**
 * @param {Array<SpeechSynthesisVoice>} voices
 * @param {Array<string>} languages ISO language codes
 * @param {string} fallbackLanguage
 * @returns {Promise<SpeechSynthesisVoice | undefined>}
 */
export async function getTopVoice(voices, languages, fallbackLanguage) {
  const { highQuality, standard } = await rankAndCategorizeVoices(
    voices,
    languages,
    fallbackLanguage,
  );
  return highQuality[0] ?? standard[0];
}

/**
 *
 * @param {Array<SpeechSynthesisVoice>} voices
 * @param {Array<string>} languages ISO language codes
 * @param {string} fallbackLanguage
 */
export function getFilteredVoices(voices, languages, fallbackLanguage) {
  const allVoices = new Set();
  languages.forEach((l) =>
    filterVoicesForLanguage(voices, l).forEach((v) => allVoices.add(v)),
  );
  return allVoices.size > 0
    ? [...allVoices]
    : filterVoicesForLanguage(voices, fallbackLanguage);
}

/**
 *
 * @param {Array<SpeechSynthesisVoice>} voices
 * @param {string} language ISO language code
 */
function filterVoicesForLanguage(voices, language) {
  return voices.filter(
    (f) => f.lang === language || f.lang.startsWith(language),
  );
}
